import { z } from 'zod';

export const NOTIFICATION_LOG_VERSION = 1;

export const NotificationStatus = {
  Sent: 'sent',
  Failed: 'failed',
  Scheduled: 'scheduled',
} as const;

export const NotificationEntryKind = {
  Partner: 'partner',
  Fallback: 'fallback',
} as const;

const groupRecipientSchema = z.object({
  channel: z.string(),
  destination: z.string(),
});

const agentEntrySchema = z.object({
  iteration: z.number().int(),
  status: z.string(),
  at: z.string(),
  scheduleId: z.string().optional(),
  userId: z.string(),
  name: z.string(),
  role: z.string(),
  channel: z.string(),
  destination: z.string(),
  reason: z.string().optional(),
});

const groupEntrySchema = z.object({
  kind: z.enum([NotificationEntryKind.Partner, NotificationEntryKind.Fallback]),
  iteration: z.number().int().optional(),
  status: z.string(),
  at: z.string(),
  recipients: z.array(groupRecipientSchema),
});

const unrecognizedEntrySchema = z.looseObject({
  status: z.string().optional(),
  at: z.string().optional(),
});

const notificationEntrySchema = z.union([
  agentEntrySchema,
  groupEntrySchema,
  unrecognizedEntrySchema,
]);

export const signalNotificationLogSchema = z.object({
  version: z.number().int(),
  notifications: z.array(notificationEntrySchema),
});

export type GroupRecipient = z.infer<typeof groupRecipientSchema>;
export type AgentEntry = z.infer<typeof agentEntrySchema>;
export type GroupEntry = z.infer<typeof groupEntrySchema>;
export type NotificationEntry = z.infer<typeof notificationEntrySchema>;
export type SignalNotificationLog = z.infer<typeof signalNotificationLogSchema>;

export const isAgentEntry = (entry: NotificationEntry): entry is AgentEntry =>
  'userId' in entry && 'channel' in entry;

export const isGroupEntry = (entry: NotificationEntry): entry is GroupEntry =>
  'recipients' in entry;

export function parseSignalNotificationLog(
  raw: string | null | undefined,
): SignalNotificationLog | null {
  if (!raw) return null;

  try {
    const result = signalNotificationLogSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
